import { CalendarDays } from 'lucide-react';
import { Skeleton } from '@/components/ui/skeleton';
import { useMyTimeOff } from '@/hooks/use-dashboard';
import { WidgetCard } from '../widget-card';
import { Chip, type ChipColor } from '../chip';
import { asArray, formatDay } from '../widget-format';

/** Format a day count, trimming trailing `.0` and pluralizing. */
function daysLabel(n: number): string {
  // A count that arrived missing would otherwise read as "NaN days" beside a date this widget
  // already renders as a placeholder.
  if (!Number.isFinite(n)) return '—';
  const rounded = Math.round(n * 10) / 10;
  const value = Number.isInteger(rounded) ? rounded : rounded.toFixed(1);
  return `${value} day${Math.abs(rounded) === 1 ? '' : 's'}`;
}

/**
 * Balance pill: green when days remain, gray at zero, red when over-drawn (negative).
 *
 * Takes the balance as the API declares it — `number | null` — rather than a `number` the caller
 * had to coerce. That coercion was the whole bug: `remaining ?? 0` collapsed an unreadable balance
 * to zero before this guard could see it, and the card told the employee "0 days left". Of every
 * figure on the company's landing page this is the one someone acts on, and a credible wrong
 * answer is worse here than an admitted blank.
 */
export function balanceChip(
  remaining: number | null | undefined,
): { color: ChipColor; label: string } {
  if (typeof remaining !== 'number' || !Number.isFinite(remaining)) {
    return { color: 'gray', label: '—' };
  }
  const rounded = Math.round(remaining * 10) / 10;
  if (rounded > 0) return { color: 'green', label: `${daysLabel(rounded)} left` };
  if (rounded < 0) return { color: 'red', label: daysLabel(rounded) };
  return { color: 'gray', label: `${daysLabel(0)} left` };
}

/**
 * `myTimeOff` widget — remaining balance per absence type configured for the
 * signed-in employee, plus a pending-request banner. View only.
 */
export function MyTimeOffWidget() {
  const { data, isLoading, isError } = useMyTimeOff();
  const balances = asArray(data?.balances);

  return (
    <WidgetCard
      title="My Time Off"
      icon={CalendarDays}
      caption="Remaining balance per absence type that applies to you."
    >
      {isLoading ? (
        <div className="space-y-2.5">
          {Array.from({ length: 3 }).map((_, i) => (
            <div key={i} className="flex items-center justify-between">
              <Skeleton className="h-4 w-32" />
              <Skeleton className="h-5 w-20" />
            </div>
          ))}
        </div>
      ) : isError ? (
        <p className="py-4 text-center text-xs text-muted-foreground">
          Couldn&apos;t load your balances.
        </p>
      ) : balances.length === 0 ? (
        <p className="py-4 text-center text-xs text-muted-foreground">No absence types available.</p>
      ) : (
        <>
          <div>
            {balances.map((b) => {
              const chip = b.unlimited
                ? { color: 'gray' as ChipColor, label: 'Unlimited' }
                : balanceChip(b.remaining);
              return (
                <div
                  key={b.typeId}
                  className="dashboard-row flex items-center justify-between gap-3 border-b border-border py-2 last:border-0"
                >
                  <span className="min-w-0 truncate text-sm">{b.name}</span>
                  <Chip color={chip.color}>{chip.label}</Chip>
                </div>
              );
            })}
          </div>
          {data?.pending && (
            <div
              className="mt-3 rounded-lg px-3 py-2 text-xs font-medium"
              style={{ backgroundColor: 'rgba(245, 158, 11, 0.14)', color: '#f59e0b' }}
            >
              Pending: {formatDay(data.pending.startDate)} – {formatDay(data.pending.endDate)} (
              {daysLabel(data.pending.totalDays)}) · awaiting manager approval
            </div>
          )}
        </>
      )}
    </WidgetCard>
  );
}
